import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useBookings } from "../../hooks";
import { useSEO } from "../../hooks";
import { bookingAPI, userAPI } from "../../services/api";
import { format } from "date-fns";
import toast from "react-hot-toast";
import resolveImage from "../../utils/imageUrl";
import {
  FiMapPin,
  FiCalendar,
  FiClock,
  FiChevronRight,
  FiCheckCircle,
  FiPlayCircle,
} from "react-icons/fi";
import "./Dashboard.css";

const TABS = ["all", "confirmed", "in_progress", "completed"];

const TechnicianDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  useSEO({
    title: `Technician Dashboard — ${user?.name || "My Jobs"}`,
    description:
      "Manage your assigned service jobs and availability on Expert At Door.",
    canonical: "/technician/dashboard",
    noindex: true,
  });

  const [activeTab, setActiveTab] = useState("confirmed");
  const [togglingAvail, setTogglingAvail] = useState(false);
  const [isOnline, setIsOnline] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [allBookings, setAllBookings] = useState([]);

  // Filtered list for the active tab
  const { bookings, loading, refetch } = useBookings(
    activeTab === "all" ? {} : { status: activeTab },
  );

  // Fetch all jobs once for accurate stats
  useEffect(() => {
    bookingAPI
      .getAll({ limit: 1000 })
      .then(({ data }) => setAllBookings(data.data || []))
      .catch(() => {});
  }, []);

  const handleToggleAvailability = async () => {
    setTogglingAvail(true);
    try {
      await userAPI.toggleAvailability(!isOnline);
      setIsOnline(!isOnline);
      toast.success(isOnline ? "You are now offline" : "You are now online");
    } catch {
      toast.error("Failed to update availability");
    } finally {
      setTogglingAvail(false);
    }
  };

  const handleUpdateStatus = async (bookingId, status) => {
    setUpdatingId(bookingId);
    try {
      await bookingAPI.updateStatus(bookingId, status);
      toast.success(`Job marked as ${status.replace("_", " ")}`);
      refetch();
      // refresh stats too
      bookingAPI
        .getAll({ limit: 1000 })
        .then(({ data }) => setAllBookings(data.data || []))
        .catch(() => {});
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    } finally {
      setUpdatingId(null);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="dashboard-welcome">
            <div className="welcome-avatar">
              {user?.profileImage ? (
                <img src={resolveImage(user.profileImage)} alt={user.name} />
              ) : (
                <span>{user?.name?.[0]}</span>
              )}
            </div>
            <div>
              <h2>Hey, {user?.name?.split(" ")[0]} 👷</h2>
              <p>Technician Dashboard</p>
            </div>
          </div>
        </div>

        {/* Availability toggle */}
        <div className="availability-toggle">
          <button
            className={`toggle-track ${isOnline ? "on" : ""}`}
            onClick={handleToggleAvailability}
            disabled={togglingAvail}
            aria-label="Toggle availability"
          >
            <div className="toggle-thumb" />
          </button>
          <div>
            <p className="toggle-label">Job Availability</p>
            <p
              className="toggle-status"
              style={{ color: isOnline ? "var(--status-success)" : undefined }}
            >
              {isOnline ? "🟢 Online — accepting new jobs" : "🔴 Offline"}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="dashboard-stats">
          {[
            {
              label: "Assigned",
              value: allBookings.filter((b) => b.status === "confirmed").length,
              icon: "📋",
            },
            {
              label: "In Progress",
              value: allBookings.filter((b) => b.status === "in_progress")
                .length,
              icon: "🔧",
            },
            {
              label: "Completed",
              value: allBookings.filter((b) => b.status === "completed").length,
              icon: "✅",
            },
          ].map(({ label, value, icon }) => (
            <div key={label} className="stat-card">
              <span className="stat-icon">{icon}</span>
              <div>
                <span className="stat-value">{value}</span>
                <span className="stat-label">{label}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Jobs */}
        <div className="dashboard-section">
          <h3 className="section-title-sm">My Jobs</h3>

          <div className="status-tabs">
            {TABS.map((tab) => (
              <button
                key={tab}
                className={`status-tab ${activeTab === tab ? "active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "all" ? "All" : tab.replace("_", " ")}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="bookings-list">
              {Array(3)
                .fill(0)
                .map((_, i) => (
                  <div
                    key={i}
                    className="booking-card skeleton"
                    style={{ height: 130 }}
                  />
                ))}
            </div>
          ) : bookings.length === 0 ? (
            <div className="empty-state">
              <span className="empty-icon">🔧</span>
              <h4>No jobs here</h4>
              <p>
                {activeTab === "confirmed"
                  ? "No confirmed jobs assigned yet."
                  : `No ${activeTab.replace("_", " ")} jobs.`}
              </p>
            </div>
          ) : (
            <div className="bookings-list">
              {bookings.map((booking) => (
                <TechnicianJobCard
                  key={booking._id}
                  booking={booking}
                  onUpdateStatus={handleUpdateStatus}
                  updatingId={updatingId}
                  navigate={navigate}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TechnicianJobCard = ({
  booking,
  onUpdateStatus,
  updatingId,
  navigate,
}) => {
  const canStart = booking.status === "confirmed";
  const canComplete = booking.status === "in_progress";
  const busy = updatingId === booking._id;

  return (
    <div className="booking-card">
      <div className="booking-card-left">
        <div className="booking-service-img">
          {booking.serviceId?.image ? (
            <img
              src={resolveImage(booking.serviceId.image)}
              alt={booking.serviceId?.name}
            />
          ) : (
            <span>🔧</span>
          )}
        </div>
        <div className="booking-info">
          <div className="booking-top-row">
            <h4>{booking.serviceSnapshot?.name || booking.serviceId?.name}</h4>
            <span className={`badge badge-${booking.status}`}>
              {booking.status.replace("_", " ")}
            </span>
          </div>
          <p className="booking-number">#{booking.bookingNumber}</p>
          <div className="booking-meta-row">
            <span className="booking-meta-item">
              <FiCalendar size={12} />{" "}
              {format(new Date(booking.scheduledDate), "dd MMM yyyy")}
            </span>
            <span className="booking-meta-item">
              <FiClock size={12} /> {booking.scheduledTimeSlot}
            </span>
            <span className="booking-meta-item">
              <FiMapPin size={12} /> {booking.address?.city}
            </span>
          </div>
          <p className="booking-tech" style={{ color: "var(--brand-ink)" }}>
            Customer: <strong>{booking.customerId?.name}</strong>
            {booking.customerId?.phone && ` · ${booking.customerId.phone}`}
          </p>
          {booking.address && (
            <p
              className="booking-tech"
              style={{ fontSize: "0.78rem", color: "var(--brand-muted)" }}
            >
              📍 {booking.address.line1}, {booking.address.city} —{" "}
              {booking.address.pincode}
            </p>
          )}
        </div>
      </div>

      <div className="booking-card-right">
        <span className="booking-amount">₹{booking.totalAmount}</span>

        <div className="booking-actions">
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => navigate(`/booking/${booking._id}`)}
          >
            Details <FiChevronRight size={14} />
          </button>

          {canStart && (
            <button
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => onUpdateStatus(booking._id, "in_progress")}
            >
              <FiPlayCircle size={14} />
              {busy ? "Starting..." : "Start Job"}
            </button>
          )}

          {canComplete && (
            <button
              className="btn btn-sm"
              style={{
                background: "var(--status-success)",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
              disabled={busy}
              onClick={() => onUpdateStatus(booking._id, "completed")}
            >
              <FiCheckCircle size={14} />
              {busy ? "Completing..." : "Mark Complete"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TechnicianDashboard;
